import {Button, Image, Space} from 'antd'
import React, {useCallback, useState} from 'react'
import useToggle from 'react-use/lib/useToggle'
import Translate from "../../modules/local/Translate";
import {PiCaretLeftBold, PiCaretRightBold} from "react-icons/pi";
import {renderIf} from "../../views/Shared";
import {HiViewfinderCircle} from "react-icons/hi2";
import {ImagePlaceholder, PhotoFallbackIcon} from "../Svgs/PhotoSvg";

/**
 * Tính chiều cao gallery dựa trên ratio của ảnh đầu tiên và số lượng ảnh.
 *
 * ratio = naturalHeight / naturalWidth của ảnh đầu tiên
 *
 * Ảnh dọc (ratio > 1):  cần gallery cao hơn để thấy nhiều hơn
 * Ảnh ngang (ratio < 1): gallery thấp hơn vừa đủ
 * Ảnh vuông (ratio ~ 1): trung bình
 *
 * Với 5+ ảnh (cột phải có 3 hàng), nhân thêm hệ số 1.4
 * để mỗi hàng vẫn đủ cao.
 */
const computeGalleryHeight = (ratio, count) => {
    let base
    if (ratio >= 1.5)       base = 520   // rất dọc (4:3 dọc, 2:3...)
    else if (ratio >= 1.1)  base = 440   // dọc vừa
    else if (ratio >= 0.85) base = 360   // gần vuông
    else if (ratio >= 0.6)  base = 300   // ngang vừa (4:3 ngang)
    else                    base = 260   // ngang rộng (16:9...)

    // 5+ ảnh: cột phải chia 3 hàng nên cần cao hơn
    if (count >= 5) base = Math.round(base * 1.4)

    return `${base}px`
}

// Fallback khi chưa load được ảnh
const DEFAULT_HEIGHT = {
    single: null,   // 1 ảnh: auto height
    multi4: '320px',
    multi5: '480px',
}

const previewConfig = {
    mask: (
        <div className="w-full h-full relative">
            <HiViewfinderCircle
                size={16}
                style={{top: 10, right: 10}}
                className="absolute text-white opacity-85 hover:opacity-100 custom-img-view-icon"
            />
        </div>
    ),
}

/**
 * GalleryImage — wrapper chuẩn cho mọi ô ảnh trong grid.
 *
 * Antd Image render: div(wrapperStyle) > span(antd-internal) > img
 * span bên trong KHÔNG tự stretch theo parent nếu chỉ dùng wrapperStyle.
 * Fix: bọc div kiểm soát size + wrapperStyle position:absolute inset:0
 *
 * Props:
 *   src        — url ảnh
 *   style      — style cho div container ngoài
 *   className  — className cho div container ngoài
 *   overlay    — node React đè lên ảnh (vd: +N badge)
 *   onLoad     — callback(e) khi img load xong, e.target có naturalWidth/Height
 *   isFirst    — true nếu là ảnh đầu tiên (dùng để đo ratio)
 */
const GalleryImage = ({src, style, className, overlay, onLoad, isFirst}) => (
    <div
        style={{position: 'relative', overflow: 'hidden', minHeight: 0, ...style}}
        className={className}
    >
        <Image
            preview={previewConfig}
            src={src}
            fallback={PhotoFallbackIcon}
            placeholder={<ImagePlaceholder/>}
            wrapperStyle={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
            }}
            style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                cursor: 'pointer',
            }}
            onLoad={isFirst ? onLoad : undefined}
        />
        {overlay && (
            <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '1.5rem',
                fontWeight: 600,
                pointerEvents: 'none',
                zIndex: 1,
            }}>
                {overlay}
            </div>
        )}
    </div>
)

const GridImage = ({images, galleryH, onFirstImageLoad}) => {
    const count = images.length
    const gap = '2px'

    // 1 ảnh: tự co theo tỉ lệ gốc, không crop, không fix height
    if (count === 1) {
        return (
            <div style={{background: '#000', width: '100%'}}>
                <Image
                    preview={previewConfig}
                    src={images[0]}
                    fallback={PhotoFallbackIcon}
                    placeholder={<ImagePlaceholder/>}
                    wrapperStyle={{width: '100%'}}
                    style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '600px',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
            </div>
        )
    }

    // 2 ảnh: 2 cột bằng nhau
    if (count === 2) {
        return (
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap, height: galleryH}}>
                <GalleryImage src={images[0]} isFirst onLoad={onFirstImageLoad}/>
                <GalleryImage src={images[1]}/>
            </div>
        )
    }

    // 3 ảnh: trái full height, phải 2 hàng
    if (count === 3) {
        return (
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap, height: galleryH}}>
                <GalleryImage src={images[0]} isFirst onLoad={onFirstImageLoad}/>
                <div style={{display: 'flex', flexDirection: 'column', gap, minHeight: 0}}>
                    <GalleryImage src={images[1]} style={{flex: 1}}/>
                    <GalleryImage src={images[2]} style={{flex: 1}}/>
                </div>
            </div>
        )
    }

    // 4 ảnh: 2×2
    if (count === 4) {
        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap,
                height: galleryH,
            }}>
                <GalleryImage src={images[0]} isFirst onLoad={onFirstImageLoad}/>
                <GalleryImage src={images[1]}/>
                <GalleryImage src={images[2]}/>
                <GalleryImage src={images[3]}/>
            </div>
        )
    }

    // 5+ ảnh: cột trái 2 hàng, cột phải 3 hàng, ô cuối overlay +N
    const display = images.slice(0, 5)
    const remaining = images.length - 5

    return (
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap, height: galleryH}}>
            {/* cột trái: 2 hàng */}
            <div style={{display: 'flex', flexDirection: 'column', gap, minHeight: 0}}>
                <GalleryImage src={display[0]} style={{flex: 1}} isFirst onLoad={onFirstImageLoad}/>
                <GalleryImage src={display[1]} style={{flex: 1}}/>
            </div>
            {/* cột phải: 3 hàng */}
            <div style={{display: 'flex', flexDirection: 'column', gap, minHeight: 0}}>
                <GalleryImage src={display[2]} style={{flex: 1}}/>
                <GalleryImage src={display[3]} style={{flex: 1}}/>
                <GalleryImage
                    src={display[4]}
                    style={{flex: 1}}
                    overlay={remaining > 0 ? `+${remaining}` : null}
                />
            </div>
        </div>
    )
}

export function GalleryEmbed({images}) {
    const [open, toggle] = useToggle()
    const count = images?.length ?? 0

    // galleryH = null nghĩa là chưa load xong ảnh đầu, dùng fallback
    const fallbackH = count >= 5
        ? DEFAULT_HEIGHT.multi5
        : DEFAULT_HEIGHT.multi4
    const [galleryH, setGalleryH] = useState(fallbackH)

    const handleFirstImageLoad = useCallback((e) => {
        const {naturalWidth, naturalHeight} = e.target
        if (!naturalWidth || !naturalHeight) return
        const ratio = naturalHeight / naturalWidth
        setGalleryH(computeGalleryHeight(ratio, count))
    }, [count])

    return (
        <Translate>
            {(t) => renderIf(
                images,
                <Image.PreviewGroup preview={{movable: false}}>
                    {/* Render ảnh 6+ ẩn để PreviewGroup nhận đủ danh sách */}
                    {images.length > 5 && images.slice(5).map((src, i) => (
                        <Image
                            key={i}
                            src={src}
                            fallback={PhotoFallbackIcon}
                            style={{display: 'none'}}
                            wrapperStyle={{display: 'none'}}
                        />
                    ))}
                    <div className="w-full mb-3 background-200 overflow-hidden relative">
                        <GridImage
                            images={images}
                            galleryH={galleryH}
                            onFirstImageLoad={handleFirstImageLoad}
                        />
                        {renderIf(
                            count >= 2,
                            <Space.Compact style={{rowGap: 0}} size="small" className="button-gallery-images absolute bottom-0 right-0 p-2 z-10">
                                <Button
                                    type="primary"
                                    style={{
                                        border: 'none',
                                        background: 'rgba(0, 0, 0, 0.53)',
                                        borderTopLeftRadius: '0.3rem',
                                        borderBottomLeftRadius: '0.3rem',
                                    }}
                                    icon={<PiCaretLeftBold size={12}/>}
                                    className="flex flex-center no-shadow"
                                />
                                <Button
                                    type="primary"
                                    style={{border: 'none', background: 'rgba(0, 0, 0, 0.53)'}}
                                    className="font-normal flex flex-center no-shadow"
                                >
                                    <span style={{fontSize: '0.6rem'}} className="italic lowercase leading-snug">
                                        {`1 - ${count} ${t('image')}`}
                                    </span>
                                </Button>
                                <Button
                                    type="primary"
                                    style={{
                                        border: 'none',
                                        background: 'rgba(0, 0, 0, 0.53)',
                                        borderTopRightRadius: '0.3rem',
                                        borderBottomRightRadius: '0.3rem',
                                    }}
                                    icon={<PiCaretRightBold size={12}/>}
                                    className="flex flex-center no-shadow"
                                />
                            </Space.Compact>
                        )}
                    </div>
                </Image.PreviewGroup>
            )}
        </Translate>
    )
}